/** FROZEN (W0-contracts). Spaces owns items/projects and these account-scoped adapters. */
import { lazy, Suspense, type ReactElement } from 'react';
import type { Id } from '@/model';
import type { TaskSlot } from '@/app/paths';
import { Spinner } from '@/ui';
import { useCopy } from './components';
import { useBoardFacts, usePresentation } from './data/presentation';
const Detail = lazy(async () => ({ default: (await import('./items/Detail')).ItemDetailScreen }));
const Editor = lazy(async () => ({ default: (await import('./items/Editor')).ItemEditor }));
const ProjectForm = lazy(async () => ({ default: (await import('./projects/NewProject')).NewProjectForm }));
const MessageForm = lazy(() => import('./items/MessageSource'));
const ChatProjectForm = lazy(async () => ({ default: (await import('./projects/ChatProject')).ChatProjectForm }));
function Loading() { const { t } = useCopy(); return <Spinner label={t('ui.loading')}/>; }
export interface ItemDetailProps {
    itemId: Id;
    onOpenEvent?: (itemId: Id, occurrenceKey?: string) => void;
}
export function ItemDetail(props: ItemDetailProps): ReactElement | null { return <Suspense fallback={<Loading />}><Detail {...props}/></Suspense>; }
export interface CreateTaskProps {
    kind: 'task';
    slot: TaskSlot;
    onCreated(itemId: Id): void;
    onCancel(): void;
}
export function CreateTask(props: CreateTaskProps): ReactElement | null { return <Suspense fallback={<Loading />}><Editor kind={props.kind} slot={props.slot} onDone={props.onCreated} onCancel={props.onCancel}/></Suspense>; }
export interface NewProjectProps {
    onDone(projectId: Id): void;
    onCancel(): void;
}
export function NewProject(props: NewProjectProps): ReactElement | null { return <Suspense fallback={<Loading />}><ProjectForm {...props}/></Suspense>; }
export interface CreateItemFromMessageProps {
    messageId: Id;
    onClose(): void;
}
/** Resolve fresh authorized source. A URL or caller never supplies copied message text. */
export function CreateItemFromMessage(props: CreateItemFromMessageProps): ReactElement | null { return <Suspense fallback={<Loading />}><MessageForm {...props}/></Suspense>; }
export interface ChatProjectSheetProps {
    conversationId: Id;
    mode: 'make' | 'move';
    onDone(projectId: Id): void;
    onCancel(): void;
}
export function ChatProjectSheet(props: ChatProjectSheetProps): ReactElement | null { return <Suspense fallback={<Loading />}><ChatProjectForm {...props}/></Suspense>; }
export interface CalendarBoardFacts {
    realmId: Id;
    accountId: Id;
    generation: number;
    complete: boolean;
    boards: readonly {
        id: Id;
        title: string;
        itemIds: readonly Id[];
    }[];
}
export interface ItemLocalPresentation {
    boardIds: readonly Id[] | null;
    includeNoBoard: boolean;
    status: 'all' | 'open' | 'done' | {
        state: string;
    };
}
/** No N+1 membership reads. Undefined/incomplete means All, never a known empty board. */
export function useCalendarBoardFacts(): CalendarBoardFacts | undefined { return useBoardFacts(); }
/** One local board/status preference owned by Spaces, shared with Calendar; never server authority. */
export function useItemLocalPresentation(): readonly [
    ItemLocalPresentation,
    (next: ItemLocalPresentation) => void
] {
    return usePresentation();
}
